import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { useError } from '../contexts/ErrorContext';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSIONS } from '../utils/permissions';

interface Staff {
  id: string;
  name: string;
  status: 'Hoạt động' | 'Đã nghỉ';
  role: string;
}

interface LatestCheckInOut {
  id: string;
  staff_id: string;
  check_type: 'check_in' | 'check_out';
  check_time: string;
  location_latitude: number;
  location_longitude: number;
  location_address?: string;
  schedule_id?: string;
  created_at: string;
}

interface UpcomingSchedule {
  id: string;
  staff_id: string;
  work_date: string;
  shift: string;
  staff_name: string;
  staff_status: string;
  staff_role?: string;
}

export interface MediaItem {
  id: string;
  file: File;
  previewUrl: string;
  type: 'image' | 'video';
  name: string;
  size: number;
}

export const CheckInOutPage: React.FC = () => {
  const { setLoading } = useLoading();
  const { error, setError, clearError } = useError();
  const { user, can } = useAuth();
  // Chấm công hộ người khác cần quyền Quản lý chấm công
  const canCheckInForOthers = can(PERMISSIONS.attendance);

  const [staff, setStaff] = useState<Staff[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<string>('');
  const [checkType, setCheckType] = useState<'check_in' | 'check_out'>('check_in');
  const [staffSearch, setStaffSearch] = useState('');
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  const [latestCheck, setLatestCheck] = useState<LatestCheckInOut | null>(null);
  const [upcomingSchedule, setUpcomingSchedule] = useState<UpcomingSchedule | null>(null);
  const [location, setLocation] = useState<{ latitude: number; longitude: number; address?: string } | null>(null);
  const [locationError, setLocationError] = useState<string>('');
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [showLocationPermissionModal, setShowLocationPermissionModal] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [blockUntil, setBlockUntil] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const [lastRequestKey, setLastRequestKey] = useState<string | null>(null);
  const [lastRequestTime, setLastRequestTime] = useState<number>(0);

  // Media (Photos / Videos) State
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [isRecordingVideo, setIsRecordingVideo] = useState<boolean>(false);
  const [recordingSeconds, setRecordingSeconds] = useState<number>(0);
  const [cameraError, setCameraError] = useState<string>('');
  const [previewItemModal, setPreviewItemModal] = useState<MediaItem | null>(null);
  const [cameraFlash, setCameraFlash] = useState<boolean>(false);

  const staffSearchRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  const recordingTimerRef = useRef<any>(null);
  const thumbnailStripRef = useRef<HTMLDivElement>(null);

  // Auto-scroll thumbnail strip when a new photo/video is captured
  useEffect(() => {
    if (thumbnailStripRef.current) {
      thumbnailStripRef.current.scrollTo({
        left: thumbnailStripRef.current.scrollWidth,
        behavior: 'smooth'
      });
    }
  }, [mediaList.length]);

  // Load staff list and restore from localStorage or logged-in user
  useEffect(() => {
    const loadStaff = async () => {
      try {
        const response = await fetch('/api/staff?all=true');
        const data = await response.json();
        if (data.success) {
          setStaff(data.data);

          // Priority 1: Use logged-in user's staff_id if available (highest priority)
          if (user?.id) {
            const loggedInStaff = data.data.find((s: Staff) => s.id === user.id && s.status === 'Hoạt động');
            if (loggedInStaff) {
              setSelectedStaffId(user.id);
              setStaffSearch(loggedInStaff.name);

              // Restore checkType from localStorage for logged-in user
              try {
                const savedCheckType = localStorage.getItem('checkinout_last_check_type');
                if (savedCheckType) {
                  // If last check was check_in, suggest check_out and vice versa
                  const oppositeType = savedCheckType === 'check_in' ? 'check_out' : 'check_in';
                  setCheckType(oppositeType);
                }
              } catch (err) {
                console.error('Error restoring checkType from localStorage:', err);
              }

              // Clear any conflicting localStorage data for staff_id
              const savedStaffId = localStorage.getItem('checkinout_last_staff_id');
              if (savedStaffId && savedStaffId !== user.id) {
                localStorage.removeItem('checkinout_last_staff_id');
              }
            }
          } else {
            // Priority 2: Restore from localStorage only if no logged-in user
            try {
              const savedStaffId = localStorage.getItem('checkinout_last_staff_id');
              const savedCheckType = localStorage.getItem('checkinout_last_check_type') == 'check_in' ? 'check_out' : 'check_in';

              if (savedStaffId && savedCheckType) {
                // Check if staff still exists in the list
                const staffExists = data.data.some((s: Staff) => s.id === savedStaffId && s.status === 'Hoạt động');

                if (staffExists) {
                  setSelectedStaffId(savedStaffId);
                  setCheckType(savedCheckType);
                  // Find staff name for search field
                  const staffMember = data.data.find((s: Staff) => s.id === savedStaffId);
                  if (staffMember) {
                    setStaffSearch(staffMember.name);
                  }
                } else {
                  // Clear invalid saved data
                  localStorage.removeItem('checkinout_last_staff_id');
                  localStorage.removeItem('checkinout_last_check_type');
                }
              }
            } catch (err) {
              console.error('Error restoring from localStorage:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error loading staff:', err);
      }
    };
    loadStaff();
  }, [user]);

  // Get location when component mounts
  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Load latest check and upcoming schedule when staff is selected
  useEffect(() => {
    if (selectedStaffId) {
      loadLatestCheck();
    } else {
      setLatestCheck(null);
      setUpcomingSchedule(null);
      setWarningMessage('');
    }
  }, [selectedStaffId]);

  // Check for warning when checkType or latestCheck changes
  useEffect(() => {
    if (latestCheck && selectedStaffId) {
      checkForWarning();
    } else {
      setWarningMessage('');
    }
  }, [checkType, latestCheck, selectedStaffId]);

  // Countdown timer for block period
  useEffect(() => {
    if (blockUntil === null) {
      setRemainingSeconds(0);
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.ceil((blockUntil - now) / 1000);

      if (remaining <= 0) {
        setBlockUntil(null);
        setRemainingSeconds(0);
        clearInterval(interval);
      } else {
        setRemainingSeconds(remaining);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [blockUntil]);

  // Calculate distance between two coordinates in meters (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  const getCurrentLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Trình duyệt không hỗ trợ định vị');
      return;
    }

    setLoading(true, 'Đang lấy vị trí...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        console.log('Latitude:', latitude);
        console.log('Longitude:', longitude);

        // Check cached location
        let address = '';
        const CACHE_THRESHOLD_METERS = 10; // Use cache if within 20 meters

        try {
          const cachedLocationStr = localStorage.getItem('checkinout_cached_location');
          if (cachedLocationStr) {
            const cachedLocation = JSON.parse(cachedLocationStr);
            const distance = calculateDistance(
              latitude,
              longitude,
              cachedLocation.latitude,
              cachedLocation.longitude
            );

            if (distance <= CACHE_THRESHOLD_METERS && cachedLocation.address) {
              // Use cached address if within threshold
              console.log(`Using cached address (distance: ${distance.toFixed(2)}m)`);
              address = cachedLocation.address;
              setLocation({ latitude, longitude, address });
              setLoading(false);
              return;
            }
          }
        } catch (err) {
          console.error('Error reading cached location:', err);
        }

        // Get address from backend API (avoids CORS issues)
        try {
          const response = await fetch('/api/geocode/reverse', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ latitude, longitude })
          });

          const data = await response.json();
          if (data.success && data.address) {
            address = data.address;

            // Save to cache
            try {
              const locationCache = {
                latitude,
                longitude,
                address,
                timestamp: Date.now()
              };
              localStorage.setItem('checkinout_cached_location', JSON.stringify(locationCache));
            } catch (err) {
              console.error('Error saving location to cache:', err);
            }
          } else {
            // Fallback: use coordinates as address
            address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          }
        } catch (err) {
          console.error('Error getting address:', err);
          // Fallback: use coordinates as address if API fails
          address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        }

        // Ensure address is never empty
        if (!address || address.trim() === '') {
          address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        }

        setLocation({ latitude, longitude, address });
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        let errorMessage = 'Không thể lấy vị trí';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Bạn đã từ chối quyền truy cập vị trí. Vui lòng cấp quyền để tiếp tục.';
            setPermissionDenied(true);
            setShowLocationPermissionModal(true);
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Thông tin vị trí không khả dụng';
            break;
          case error.TIMEOUT:
            errorMessage = 'Hết thời gian chờ lấy vị trí';
            break;
        }
        setLocationError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000 // Cache for 1 minute to speed up subsequent requests
      }
    );
  };

  const loadLatestCheck = async () => {
    try {
      const response = await fetch(`/api/checkinout/latest/${selectedStaffId}`);
      const data = await response.json();
      if (data.success) {
        setLatestCheck(data.data);
      }
    } catch (err) {
      console.error('Error loading latest check:', err);
    }
  };

  const checkForWarning = () => {
    if (!latestCheck) {
      setWarningMessage('');
      return;
    }

    // If latest check is check_in and user selects check_in, show warning
    if (latestCheck.check_type === 'check_in' && checkType === 'check_in') {
      setWarningMessage('⚠️ Bạn đã check-in gần đây. Bạn có chắc muốn check-in lại?');
    }
    // If latest check is check_out and user selects check_out, show warning
    else if (latestCheck.check_type === 'check_out' && checkType === 'check_out') {
      setWarningMessage('⚠️ Bạn đã check-out gần đây. Bạn có chắc muốn check-out lại?');
    }
    // If latest check is check_out and user selects check_in, it's normal (new shift)
    else if (latestCheck.check_type === 'check_out' && checkType === 'check_in') {
      setWarningMessage('');
    }
    // If latest check is check_in and user selects check_out, it's normal
    else if (latestCheck.check_type === 'check_in' && checkType === 'check_out') {
      setWarningMessage('');
    }
  };

  // Filter staff based on search
  const filteredStaffForSearch = staff.filter(s => {
    if (s.status !== 'Hoạt động') return false;
    if (!staffSearch.trim()) return true;
    const searchLower = staffSearch.toLowerCase();
    return s.name.toLowerCase().includes(searchLower) ||
      (s.role && s.role.toLowerCase().includes(searchLower));
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (staffSearchRef.current && !staffSearchRef.current.contains(event.target as Node)) {
        setShowStaffDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Camera and Media Handlers
  const stopCameraStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecordingVideo(false);
    setRecordingSeconds(0);
    setIsCameraActive(false);
  };

  const startCameraStream = async (mode: 'user' | 'environment' = facingMode) => {
    setCameraError('');
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Trình duyệt không hỗ trợ truy cập máy ảnh trực tiếp');
        return;
      }

      let stream: MediaStream;
      const isMobileDevice = checkIsMobile();
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: mode,
            aspectRatio: { ideal: isMobileDevice ? 4 / 3 : 16 / 9 }
          },
          audio: false
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: mode },
          audio: false
        });
      }

      mediaStreamRef.current = stream;
      setIsCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(e => console.error('Video play error:', e));
      }
    } catch (err: any) {
      console.error('Camera access error:', err);
      let msg = 'Không thể mở máy ảnh. Vui lòng cấp quyền truy cập máy ảnh.';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        msg = 'Bạn đã từ chối quyền máy ảnh. Vui lòng cấp quyền trong cài đặt trình duyệt.';
      } else if (err.name === 'NotFoundError') {
        msg = 'Không tìm thấy thiết bị máy ảnh trên máy này.';
      }
      setCameraError(msg);
      setIsCameraActive(false);
    }
  };

  const toggleCameraFacing = () => {
    const nextMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(nextMode);
    startCameraStream(nextMode);
  };

  const addMediaFile = (file: File, type: 'image' | 'video') => {
    if (mediaList.length >= 5) {
      alert('Đã đạt giới hạn tối đa 5 hình ảnh hoặc video.');
      return false;
    }

    if (file.size > 50 * 1024 * 1024) {
      alert(`Tệp "${file.name}" vượt quá kích thước cho phép (tối đa 50MB).`);
      return false;
    }

    const previewUrl = URL.createObjectURL(file);
    const newItem: MediaItem = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      file,
      previewUrl,
      type,
      name: file.name,
      size: file.size
    };

    setMediaList(prev => [...prev, newItem]);
    clearError();
    return true;
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || mediaList.length >= 5) return;
    const video = videoRef.current;
    if (!video.videoWidth || !video.videoHeight) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Shutter flash animation
    setCameraFlash(true);
    setTimeout(() => setCameraFlash(false), 120);

    canvas.toBlob((blob) => {
      if (blob) {
        const fileName = `checkin_photo_${Date.now()}.jpg`;
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        addMediaFile(file, 'image');
      }
    }, 'image/jpeg', 0.9);
  };

  const startVideoRecording = async () => {
    if (mediaList.length >= 5) {
      alert('Đã đạt giới hạn tối đa 5 hình ảnh hoặc video.');
      return;
    }

    try {
      let stream = mediaStreamRef.current;
      if (!stream) {
        await startCameraStream(facingMode);
        stream = mediaStreamRef.current;
      }
      if (!stream) return;

      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const tracks = [...stream.getVideoTracks(), ...audioStream.getAudioTracks()];
        stream = new MediaStream(tracks);
      } catch {
        // Continue with video-only if audio not available
      }

      recordedChunksRef.current = [];
      let mimeType = 'video/webm;codecs=vp8,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : 'video/webm';
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const ext = recorder.mimeType.includes('mp4') ? 'mp4' : 'webm';
        const blob = new Blob(recordedChunksRef.current, { type: recorder.mimeType || 'video/webm' });
        const fileName = `checkin_video_${Date.now()}.${ext}`;
        const file = new File([blob], fileName, { type: recorder.mimeType || 'video/webm' });
        addMediaFile(file, 'video');
      };

      recorder.start(1000);
      setIsRecordingVideo(true);
      setRecordingSeconds(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(prev => {
          if (prev >= 15) {
            stopVideoRecording();
            return 15;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Video recording error:', err);
      alert('Không thể bắt đầu quay video trên trình duyệt này.');
    }
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecordingVideo(false);
  };

  const removeMediaItem = (id: string) => {
    setMediaList(prev => {
      const item = prev.find(m => m.id === id);
      if (item) {
        URL.revokeObjectURL(item.previewUrl);
      }
      return prev.filter(m => m.id !== id);
    });
  };

  // Cleanup camera stream and object URLs on unmount
  useEffect(() => {
    return () => {
      stopCameraStream();
      mediaList.forEach(m => URL.revokeObjectURL(m.previewUrl));
    };
  }, []);

  // Update video element when camera is active
  useEffect(() => {
    if (isCameraActive && videoRef.current && mediaStreamRef.current) {
      videoRef.current.srcObject = mediaStreamRef.current;
      videoRef.current.play().catch(e => console.error('Video play error:', e));
    }
  }, [isCameraActive]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    // Check if button is blocked
    if (blockUntil !== null && Date.now() < blockUntil) {
      setError(`Vui lòng đợi ${remainingSeconds} giây trước khi thực hiện lại`);
      return;
    }

    // Check if currently submitting
    if (isSubmitting) {
      setError('Đang xử lý yêu cầu, vui lòng đợi...');
      return;
    }

    if (!selectedStaffId) {
      setError('Vui lòng chọn nhân viên');
      return;
    }

    if (!location) {
      setError('Vui lòng cho phép truy cập vị trí để tiếp tục');
      return;
    }

    if (!location.address || location.address.trim() === '') {
      setError('Không thể lấy địa chỉ. Vui lòng thử lại.');
      return;
    }

    // Validate media count (1 to 5 files required)
    if (mediaList.length < 1) {
      setError('Vui lòng chụp hoặc đính kèm ít nhất 1 hình ảnh hoặc video để điểm danh.');
      return;
    }

    if (mediaList.length > 5) {
      setError('Chỉ được gửi tối đa 5 hình ảnh hoặc video.');
      return;
    }

    // Create request key to detect duplicate requests
    const requestKey = `${selectedStaffId}_${checkType}_${location.latitude.toFixed(6)}_${location.longitude.toFixed(6)}`;
    const now = Date.now();

    // Check for duplicate request within 15 seconds
    if (lastRequestKey === requestKey && (now - lastRequestTime) < 15000) {
      const timeLeft = Math.ceil((15000 - (now - lastRequestTime)) / 1000);
      setError(`Vui lòng đợi ${timeLeft} giây trước khi gửi lại request tương tự`);
      return;
    }

    // Show confirmation if there's a warning
    if (warningMessage) {
      const confirmed = window.confirm(
        `${warningMessage}\n\nBạn có chắc chắn muốn tiếp tục?`
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setLoading(true, checkType === 'check_in' ? 'Đang tải minh chứng và check-in...' : 'Đang tải minh chứng và check-out...');

      // Update last request info
      setLastRequestKey(requestKey);
      setLastRequestTime(now);

      const formData = new FormData();
      formData.append('staffId', selectedStaffId);
      formData.append('checkType', checkType);
      formData.append('latitude', String(location.latitude));
      formData.append('longitude', String(location.longitude));
      formData.append('address', location.address);
      if (upcomingSchedule?.id) {
        formData.append('scheduleId', upcomingSchedule.id);
      }
      for (const item of mediaList) {
        formData.append('media', item.file, item.name);
      }

      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/checkinout', {
        method: 'POST',
        headers,
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        stopCameraStream();
        // Block button for 15 seconds
        const blockTime = Date.now() + 15000;
        setBlockUntil(blockTime);

        alert(data.message || (checkType === 'check_in' ? 'Check-in thành công!' : 'Check-out thành công!'));

        // Save to localStorage before resetting
        try {
          localStorage.setItem('checkinout_last_staff_id', selectedStaffId);
          localStorage.setItem('checkinout_last_check_type', checkType);
          window.location.reload();
        } catch (err) {
          console.error('Error saving to localStorage:', err);
        }
      } else {
        setError(data.error || 'Có lỗi xảy ra');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Error submitting check-in/out:', err);
      setError('Có lỗi xảy ra khi gửi yêu cầu');
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const checkIsMobile = () => window.innerWidth < 768 || (window.innerHeight <= 500 && window.innerWidth <= 950);
  const [isMobile, setIsMobile] = useState(checkIsMobile());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Detect device type
  const detectDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

    // iOS detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      return 'ios';
    }

    // Android detection
    if (/android/i.test(userAgent)) {
      return 'android';
    }

    return 'other';
  };

  const deviceType = detectDevice();

  return (
    <div className="checkinout-page" style={{
      padding: isMobile ? '0' : '20px',
      maxWidth: '800px',
      margin: '0 auto',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <div className="container" style={{
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
        padding: 0,
        width: '100%'
      }}>
        <div className="header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? '8px' : '16px',
          padding: isMobile ? '0 2px' : '0'
        }}>
          <h1 style={{
            fontSize: isMobile ? '19px' : '24px',
            margin: isMobile ? '4px 0 8px' : '0 0 16px',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: '#0f172a'
          }}>
            Điểm danh ca làm
          </h1>
          {user?.isAdmin && (
            <Link
              to={user.homePath}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: isMobile ? '6px 10px' : '8px 14px',
                background: '#8d2a2c',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: isMobile ? '12px' : '13px'
              }}
            >
              Về trang quản trị
            </Link>
          )}
        </div>

        <form onSubmit={handleSubmit} style={{
          background: 'white',
          padding: isMobile ? '16px 12px' : '28px',
          borderRadius: isMobile ? '14px' : '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Staff Selection */}
          {!canCheckInForOthers ? (
            <div className="form-group" style={{ marginBottom: '22px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#0f172a', fontSize: '14px' }}>
                Tài khoản điểm danh <span style={{ color: '#16a34a' }}>✓</span>
              </label>
              <div style={{
                padding: '12px 16px',
                background: '#f8fafc',
                border: '1.5px solid #e2e8f0',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '50%',
                    background: '#fee2e2',
                    color: '#8d2a2c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '15px'
                  }}>
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'NV'}
                  </div>
                  <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '14.5px' }}>
                    {user?.name || staffSearch}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="form-group" style={{ marginBottom: '22px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontWeight: 600, color: '#0f172a', fontSize: '14px' }}>
                  Chọn nhân viên <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <span style={{ fontSize: '12px', color: '#8d2a2c', fontWeight: 600, background: '#fee2e2', padding: '2px 8px', borderRadius: '6px' }}>
                  Admin chọn được NV khác
                </span>
              </div>
              <div
                ref={staffSearchRef}
                style={{ position: 'relative', width: '100%' }}
              >
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Tìm theo tên hoặc vai trò..."
                    value={staffSearch}
                    onChange={(e) => {
                      setStaffSearch(e.target.value);
                      setShowStaffDropdown(true);
                    }}
                    onFocus={() => setShowStaffDropdown(true)}
                    style={{
                      width: '100%',
                      padding: '11px 38px 11px 14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  <i
                    className="fas fa-search"
                    style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#94a3b8',
                      pointerEvents: 'none'
                    }}
                  />
                </div>

                {showStaffDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      maxHeight: '280px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                  >
                    {filteredStaffForSearch.length > 0 ? (
                      filteredStaffForSearch.map(s => (
                        <div
                          key={s.id}
                          onClick={() => {
                            setSelectedStaffId(s.id);
                            setStaffSearch(s.name);
                            setShowStaffDropdown(false);
                          }}
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            backgroundColor: selectedStaffId === s.id ? '#fdf2f2' : 'white',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedStaffId !== s.id) {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedStaffId !== s.id) {
                              e.currentTarget.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '13.5px' }}>
                            {s.name}
                          </div>
                          {s.role && (
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                              {s.role}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '14px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                        Không tìm thấy nhân viên
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Check Type Selection */}
          <div className="form-group" style={{ marginBottom: '22px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#0f172a', fontSize: '14px' }}>
              Hình thức điểm danh <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: isMobile ? '10px' : '14px', flexDirection: isMobile ? 'column' : 'row' }}>
              <label style={{
                flex: 1,
                padding: '12px 16px',
                border: `1.5px solid ${checkType === 'check_in' ? '#16a34a' : '#e2e8f0'}`,
                borderRadius: '10px',
                cursor: 'pointer',
                backgroundColor: checkType === 'check_in' ? '#f0fdf4' : '#ffffff',
                color: checkType === 'check_in' ? '#166534' : '#334155',
                fontWeight: 600,
                transition: 'all 0.18s ease',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <input
                  type="radio"
                  name="checkType"
                  value="check_in"
                  checked={checkType === 'check_in'}
                  onChange={(e) => setCheckType(e.target.value as 'check_in' | 'check_out')}
                  style={{ marginRight: '10px', accentColor: '#16a34a' }}
                />
                Check In (Vào ca)
              </label>
              <label style={{
                flex: 1,
                padding: '12px 16px',
                border: `1.5px solid ${checkType === 'check_out' ? '#dc2626' : '#e2e8f0'}`,
                borderRadius: '10px',
                cursor: 'pointer',
                backgroundColor: checkType === 'check_out' ? '#fef2f2' : '#ffffff',
                color: checkType === 'check_out' ? '#991b1b' : '#334155',
                fontWeight: 600,
                transition: 'all 0.18s ease',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <input
                  type="radio"
                  name="checkType"
                  value="check_out"
                  checked={checkType === 'check_out'}
                  onChange={(e) => setCheckType(e.target.value as 'check_in' | 'check_out')}
                  style={{ marginRight: '10px', accentColor: '#dc2626' }}
                />
                Check Out (Tan ca)
              </label>
            </div>
          </div>

          {/* Warning Message */}
          {warningMessage && (
            <div style={{
              padding: '12px',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              marginBottom: '20px',
              color: '#856404'
            }}>
              {warningMessage}
            </div>
          )}

          {/* Location Display */}
          <div className="form-group" style={{ marginBottom: '22px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#0f172a', fontSize: '14px' }}>
              Vị trí điểm danh
            </label>
            {locationError && !permissionDenied ? (
              <div style={{
                padding: '12px 14px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#991b1b',
                fontSize: '13.5px'
              }}>
                <span>{locationError}</span>
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  style={{
                    marginLeft: '10px',
                    padding: '4px 10px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600
                  }}
                >
                  Thử lại
                </button>
              </div>
            ) : permissionDenied ? (
              <div style={{
                padding: '12px 14px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#991b1b',
                fontSize: '13.5px'
              }}>
                <span>{locationError}</span>
                <button
                  type="button"
                  onClick={() => setShowLocationPermissionModal(true)}
                  style={{
                    marginLeft: '10px',
                    padding: '4px 10px',
                    background: '#0f172a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600
                  }}
                >
                  Hướng dẫn bật định vị
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPermissionDenied(false);
                    getCurrentLocation();
                  }}
                  style={{
                    marginLeft: '8px',
                    padding: '4px 10px',
                    background: '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600
                  }}
                >
                  Thử lại
                </button>
              </div>
            ) : location ? (
              <div style={{
                padding: '8px 10px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                color: '#166534',
                fontSize: '13.5px'
              }}>
                <div style={{ marginTop: '4px', fontSize: '12.5px', color: '#15803d' }}>
                  {location.address && (
                    <div>Địa chỉ: {location.address}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  style={{
                    marginTop: '8px',
                    padding: '4px 10px',
                    background: '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600
                  }}
                >
                  Làm mới vị trí
                </button>
              </div>
            ) : (
              <div style={{
                padding: '12px 14px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                color: '#64748b',
                fontSize: '13.5px'
              }}>
                Đang lấy thông tin vị trí...
              </div>
            )}
          </div>

          {/* Photos & Videos Section (Required: 1 to 5) */}
          <div className="form-group" style={{
            marginBottom: '24px',
            background: '#f8fafc',
            border: mediaList.length >= 1 ? '1.5px solid #cbd5e1' : '1.5px solid #fecaca',
            borderRadius: '14px',
            padding: isMobile ? '14px' : '18px',
            transition: 'border-color 0.2s ease'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>
                  Chụp ảnh / video minh chứng <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ fontSize: '12.5px', color: '#64748b', marginTop: '2px' }}>
                  Bắt buộc chụp trực tiếp từ camera (1 - 5 ảnh/video, không cho phép chọn ảnh từ máy)
                </div>
              </div>
              <span style={{
                fontSize: '12.5px',
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: '20px',
                background: mediaList.length >= 1 ? '#dcfce7' : '#fee2e2',
                color: mediaList.length >= 1 ? '#15803d' : '#b91c1c',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <i className={mediaList.length >= 1 ? "fas fa-check-circle" : "fas fa-exclamation-circle"}></i>
                Đã chụp: {mediaList.length} / 5 tệp
              </span>
            </div>

            {/* Camera Trigger Button */}
            {/* Camera Trigger Button */}
            <button
              type="button"
              onClick={() => startCameraStream()}
              disabled={mediaList.length >= 5}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '13px 16px',
                background: mediaList.length >= 5 ? '#e2e8f0' : '#8d2a2c',
                color: mediaList.length >= 5 ? '#94a3b8' : '#ffffff',
                border: 'none',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '14px',
                cursor: mediaList.length >= 5 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: mediaList.length >= 5 ? 'none' : '0 2px 8px rgba(141, 42, 44, 0.25)',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="fas fa-camera" style={{ fontSize: '16px' }}></i>
              {mediaList.length >= 5
                ? 'Đã chụp đủ tối đa 5 tệp minh chứng'
                : mediaList.length === 0
                ? 'Bật máy ảnh để chụp minh chứng'
                : `Mở máy ảnh chụp thêm (${mediaList.length}/5)`}
            </button>

            {/* Camera Error */}
            {cameraError && (
              <div style={{
                marginTop: '12px',
                padding: '10px 12px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#991b1b',
                fontSize: '12.5px'
              }}>
                {cameraError}
              </div>
            )}

            {/* Media Thumbnails Grid */}
            {mediaList.length > 0 ? (
              <div style={{
                marginTop: '14px',
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(130px, 1fr))',
                gap: isMobile ? '10px' : '12px'
              }}>
                {mediaList.map((item, idx) => (
                  <div
                    key={item.id}
                    style={{
                      position: 'relative',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: '1.5px solid #e2e8f0',
                      background: '#0f172a',
                      aspectRatio: '4 / 3',
                      cursor: 'pointer'
                    }}
                    onClick={() => setPreviewItemModal(item)}
                  >
                    {item.type === 'image' ? (
                      <img
                        src={item.previewUrl}
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#1e293b',
                        color: 'white'
                      }}>
                        <i className="fas fa-play-circle" style={{ fontSize: '28px', color: '#38bdf8' }}></i>
                        <span style={{ fontSize: '11px', marginTop: '4px', opacity: 0.85 }}>Xem video</span>
                      </div>
                    )}

                    {/* Tag badge */}
                    <div style={{
                      position: 'absolute',
                      bottom: '6px',
                      left: '6px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      fontSize: '10.5px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <i className={item.type === 'image' ? 'fas fa-image' : 'fas fa-video'}></i>
                      {item.type === 'image' ? 'Ảnh' : 'Video'} #{idx + 1}
                    </div>

                    {/* Delete button */}
                    <button
                      type="button"
                      className="media-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMediaItem(item.id);
                      }}
                      title="Xóa tệp này"
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        width: '24px',
                        height: '24px',
                        minHeight: '24px',
                        maxHeight: '24px',
                        minWidth: '24px',
                        maxWidth: '24px',
                        padding: 0,
                        borderRadius: '50%',
                        background: 'rgba(220, 38, 38, 0.9)',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        zIndex: 5,
                        boxSizing: 'border-box'
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                marginTop: '14px',
                padding: '16px',
                textAlign: 'center',
                background: '#fff',
                borderRadius: '10px',
                border: '1px dashed #fca5a5',
                color: '#b91c1c',
                fontSize: '13px'
              }}>
                <i className="fas fa-camera" style={{ fontSize: '24px', color: '#f87171', display: 'block', marginBottom: '6px' }}></i>
                Chưa có ảnh hoặc video nào. Vui lòng bật máy ảnh và chụp trực tiếp ít nhất 1 ảnh/video để điểm danh.
              </div>
            )}
          </div>

          {/* Latest Check Info */}
          {latestCheck && selectedStaffId && (
            <div style={{
              padding: '12px 14px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px', color: '#0f172a', fontSize: '13px' }}>
                Lần điểm danh gần nhất:
              </div>
              <div style={{ fontSize: '13.5px', color: '#475569' }}>
                {latestCheck.check_type === 'check_in' ? 'Check In' : 'Check Out'} — {new Date(latestCheck.check_time).toLocaleString('vi-VN')}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px 14px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#991b1b',
              fontSize: '13.5px'
            }}>
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={
              !selectedStaffId ||
              !location ||
              !!locationError ||
              !location.address ||
              isSubmitting ||
              (blockUntil !== null && Date.now() < blockUntil) ||
              mediaList.length < 1 ||
              mediaList.length > 5
            }
            style={{
              width: '100%',
              padding: isMobile ? '12px' : '14px',
              background: checkType === 'check_in' ? '#16a34a' : '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: (!selectedStaffId || !location || !!locationError || isSubmitting || (blockUntil !== null && Date.now() < blockUntil) || mediaList.length < 1 || mediaList.length > 5) ? 'not-allowed' : 'pointer',
              opacity: (!selectedStaffId || !location || !!locationError || isSubmitting || (blockUntil !== null && Date.now() < blockUntil) || mediaList.length < 1 || mediaList.length > 5) ? 0.6 : 1,
              transition: 'opacity 0.2s',
              position: 'relative'
            }}
          >
            {isSubmitting ? (
              'Đang xử lý...'
            ) : blockUntil !== null && Date.now() < blockUntil ? (
              `Vui lòng đợi ${remainingSeconds}s`
            ) : mediaList.length < 1 ? (
              'Vui lòng chụp ít nhất 1 ảnh/video'
            ) : (
              checkType === 'check_in' ? 'Xác nhận Check In' : 'Xác nhận Check Out'
            )}
          </button>
        </form>

        {/* Thông tin hướng dẫn & Quy định điểm danh */}
        <div style={{
          marginTop: '20px',
          background: 'white',
          padding: isMobile ? '16px' : '20px',
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', margin: '0 0 10px' }}>
            Quy định điểm danh
          </h4>
          <ul style={{ margin: 0, paddingLeft: '18px', color: '#475569', fontSize: '13px', lineHeight: '1.7' }}>
            <li>Nhân viên bắt buộc đăng nhập tài khoản chính chủ để điểm danh.</li>
            <li>Bắt buộc chụp trực tiếp từ máy ảnh 1 - 5 hình ảnh hoặc video để làm minh chứng xác thực khi Check In / Check Out (không cho phép chọn ảnh có sẵn từ máy).</li>
            <li>Nhân viên thực hiện <strong>Check In</strong> khi bắt đầu ca và <strong>Check Out</strong> khi kết thúc ca làm việc.</li>
            <li>Vui lòng bật định vị GPS và cấp quyền truy cập vị trí trên trình duyệt thiết bị.</li>
            <li>Hệ thống lưu lại vị trí chính xác để đối soát ca làm việc.</li>
          </ul>
          <button
            type="button"
            onClick={() => setShowLocationPermissionModal(true)}
            style={{
              marginTop: '12px',
              padding: '8px 14px',
              background: '#ecf0f1',
              color: '#2980b9',
              border: '1px solid #bdc3c7',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <i className="fas fa-question-circle"></i> Hướng dẫn bật GPS trên trình duyệt
          </button>
        </div>
      </div>

      {/* Location Permission Modal */}
      {showLocationPermissionModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: isMobile ? '20px' : '40px'
          }}
          onClick={() => setShowLocationPermissionModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: isMobile ? '20px' : '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '24px', color: '#333' }}>
                <i className="fas fa-map-marker-alt" style={{ color: '#dc3545', marginRight: '10px' }}></i>
                Hướng dẫn bật định vị
              </h2>
              <button
                onClick={() => setShowLocationPermissionModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {deviceType === 'ios' ? (
              <div>
                <h3 style={{ fontSize: isMobile ? '16px' : '18px', marginBottom: '15px', color: '#333' }}>
                  <i className="fab fa-apple" style={{ marginRight: '8px' }}></i>
                  Hướng dẫn cho iPhone/iPad (Safari)
                </h3>
                <ol style={{ paddingLeft: '20px', lineHeight: '1.8', color: '#555' }}>
                  <li style={{ marginBottom: '10px' }}>
                    Mở <strong>Cài đặt</strong> (Settings) trên iPhone/iPad của bạn
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Cuộn xuống và chọn <strong>Safari</strong>
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Cuộn xuống phần <strong>"Quyền"</strong> hoặc <strong>"Privacy & Security"</strong>
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Tìm và chọn <strong>"Vị trí"</strong> (Location Services)
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Đảm bảo <strong>"Dịch vụ định vị"</strong> (Location Services) đã được bật
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Cuộn xuống và tìm <strong>"Safari"</strong> trong danh sách ứng dụng
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Chọn <strong>"Khi sử dụng ứng dụng"</strong> hoặc <strong>"Luôn luôn"</strong>
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Quay lại trình duyệt Safari và làm mới trang này
                  </li>
                </ol>
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: '#e7f3ff',
                  borderRadius: '4px',
                  border: '1px solid #b3d9ff'
                }}>
                  <strong style={{ color: '#0066cc' }}>💡 Mẹo:</strong>
                  <p style={{ margin: '8px 0 0 0', color: '#555', fontSize: '14px' }}>
                    Bạn cũng có thể bật định vị trực tiếp trong Safari bằng cách:
                    <br />
                    1. Nhấn vào biểu tượng <strong>"aA"</strong> trên thanh địa chỉ
                    <br />
                    2. Chọn <strong>"Cài đặt trang web"</strong>
                    <br />
                    3. Bật <strong>"Vị trí"</strong>
                  </p>
                </div>
              </div>
            ) : deviceType === 'android' ? (
              <div>
                <h3 style={{ fontSize: isMobile ? '16px' : '18px', marginBottom: '15px', color: '#333' }}>
                  <i className="fab fa-android" style={{ marginRight: '8px' }}></i>
                  Hướng dẫn cho Android (Chrome)
                </h3>
                <ol style={{ paddingLeft: '20px', lineHeight: '1.8', color: '#555' }}>
                  <li style={{ marginBottom: '10px' }}>
                    Mở <strong>Cài đặt</strong> (Settings) trên điện thoại Android của bạn
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Chọn <strong>"Ứng dụng"</strong> hoặc <strong>"Apps"</strong>
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Tìm và chọn <strong>"Chrome"</strong> (hoặc trình duyệt bạn đang sử dụng)
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Chọn <strong>"Quyền"</strong> hoặc <strong>"Permissions"</strong>
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Tìm <strong>"Vị trí"</strong> (Location) và chọn <strong>"Cho phép"</strong> hoặc <strong>"Khi sử dụng ứng dụng"</strong>
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Quay lại trình duyệt và làm mới trang này
                  </li>
                </ol>
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: '#e7f3ff',
                  borderRadius: '4px',
                  border: '1px solid #b3d9ff'
                }}>
                  <strong style={{ color: '#0066cc' }}>💡 Mẹo nhanh:</strong>
                  <p style={{ margin: '8px 0 0 0', color: '#555', fontSize: '14px' }}>
                    Bạn cũng có thể bật định vị trực tiếp trong Chrome:
                    <br />
                    1. Nhấn vào biểu tượng <strong>🔒</strong> hoặc <strong>"i"</strong> trên thanh địa chỉ
                    <br />
                    2. Chọn <strong>"Vị trí"</strong> và chọn <strong>"Cho phép"</strong>
                    <br />
                    3. Làm mới trang
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ fontSize: isMobile ? '16px' : '18px', marginBottom: '15px', color: '#333' }}>
                  <i className="fas fa-desktop" style={{ marginRight: '8px' }}></i>
                  Hướng dẫn chung
                </h3>
                <ol style={{ paddingLeft: '20px', lineHeight: '1.8', color: '#555' }}>
                  <li style={{ marginBottom: '10px' }}>
                    Kiểm tra cài đặt quyền của trình duyệt bạn đang sử dụng
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Tìm phần <strong>"Quyền"</strong> hoặc <strong>"Permissions"</strong> trong cài đặt trình duyệt
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Bật quyền <strong>"Vị trí"</strong> (Location) cho trang web này
                  </li>
                  <li style={{ marginBottom: '10px' }}>
                    Làm mới trang và thử lại
                  </li>
                </ol>
              </div>
            )}

            <div style={{ marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowLocationPermissionModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Đóng
              </button>
              <button
                onClick={() => {
                  setShowLocationPermissionModal(false);
                  setPermissionDenied(false);
                  getCurrentLocation();
                }}
                style={{
                  padding: '10px 20px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
                Thử lại
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Fullscreen / Popup Modal */}
      {isCameraActive && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.96)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? 0 : '20px'
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: isMobile ? '100%' : '520px',
              height: isMobile ? '100%' : '90vh',
              maxHeight: isMobile ? '100%' : '820px',
              background: '#0a0f1d',
              borderRadius: isMobile ? 0 : '16px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.8)',
              position: 'relative'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: isMobile ? 'calc(10px + env(safe-area-inset-top, 0px)) 16px 10px' : '12px 16px',
              background: 'rgba(15, 23, 42, 0.95)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>
                  Máy ảnh điểm danh
                </span>
                <span style={{
                  background: mediaList.length >= 1 ? '#16a34a' : '#475569',
                  color: 'white',
                  fontSize: '11.5px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '12px'
                }}>
                  {mediaList.length} / 5
                </span>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  type="button"
                  className="camera-icon-btn"
                  onClick={toggleCameraFacing}
                  title="Đổi camera trước/sau"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px'
                  }}
                >
                  <i className="fas fa-sync-alt"></i>
                </button>
                <button
                  type="button"
                  className="camera-icon-btn"
                  onClick={stopCameraStream}
                  title="Đóng máy ảnh"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '15px'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Viewfinder */}
            <div style={{
              position: 'relative',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#000',
              overflow: 'hidden'
            }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
                }}
              />

              {/* Viewfinder Framing Reticle */}
              <div style={{
                position: 'absolute',
                width: '75%',
                maxWidth: '320px',
                aspectRatio: '1 / 1',
                pointerEvents: 'none',
                boxSizing: 'border-box'
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '24px', height: '24px', borderTop: '3px solid rgba(255,255,255,0.7)', borderLeft: '3px solid rgba(255,255,255,0.7)', borderTopLeftRadius: '6px' }} />
                <div style={{ position: 'absolute', top: 0, right: 0, width: '24px', height: '24px', borderTop: '3px solid rgba(255,255,255,0.7)', borderRight: '3px solid rgba(255,255,255,0.7)', borderTopRightRadius: '6px' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, width: '24px', height: '24px', borderBottom: '3px solid rgba(255,255,255,0.7)', borderLeft: '3px solid rgba(255,255,255,0.7)', borderBottomLeftRadius: '6px' }} />
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: '24px', height: '24px', borderBottom: '3px solid rgba(255,255,255,0.7)', borderRight: '3px solid rgba(255,255,255,0.7)', borderBottomRightRadius: '6px' }} />
              </div>

              {/* Shutter flash animation */}
              {cameraFlash && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'white',
                  opacity: 0.8,
                  pointerEvents: 'none',
                  zIndex: 25
                }} />
              )}

              {/* Recording indicator */}
              {isRecordingVideo && (
                <div style={{
                  position: 'absolute',
                  top: '14px',
                  left: '14px',
                  background: 'rgba(220, 38, 38, 0.92)',
                  color: 'white',
                  padding: '5px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  zIndex: 15,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
                }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white' }} />
                  Đang quay: 00:{recordingSeconds < 10 ? '0' : ''}{recordingSeconds} / 15s
                </div>
              )}

              {mediaList.length >= 5 && (
                <div style={{
                  position: 'absolute',
                  bottom: '14px',
                  background: 'rgba(15, 23, 42, 0.88)',
                  color: '#38bdf8',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  zIndex: 15,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}>
                  ✓ Đã chụp đủ 5/5 tệp minh chứng
                </div>
              )}
            </div>

            {/* Modal Captured Media Strip (Dưới khung camera là danh sách hình) */}
            <div
              ref={thumbnailStripRef}
              style={{
                padding: '8px 12px',
                background: 'rgba(15, 23, 42, 0.98)',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                overflowX: 'auto',
                minHeight: '74px',
                flexShrink: 0
              }}
            >
              {mediaList.length === 0 ? (
                <div style={{
                  color: '#94a3b8',
                  fontSize: '12px',
                  textAlign: 'center',
                  width: '100%',
                  padding: '6px'
                }}>
                  Chưa có ảnh nào. Bấm nút tròn trắng bên dưới để chụp (1 - 5 ảnh).
                </div>
              ) : (
                mediaList.map((item, idx) => (
                  <div
                    key={item.id}
                    style={{
                      position: 'relative',
                      width: '56px',
                      height: '56px',
                      minWidth: '56px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1.5px solid #38bdf8',
                      background: '#000',
                      flexShrink: 0
                    }}
                  >
                    {item.type === 'image' ? (
                      <img
                        src={item.previewUrl}
                        alt={item.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#1e293b',
                        color: '#38bdf8',
                        fontSize: '18px'
                      }}>
                        <i className="fas fa-play"></i>
                      </div>
                    )}
                    <span style={{
                      position: 'absolute',
                      bottom: '2px',
                      left: '2px',
                      background: 'rgba(0,0,0,0.75)',
                      color: 'white',
                      fontSize: '9px',
                      fontWeight: 700,
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      #{idx + 1}
                    </span>
                    <button
                      type="button"
                      className="media-delete-btn"
                      onClick={() => removeMediaItem(item.id)}
                      title="Xóa tệp"
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        width: '18px',
                        height: '18px',
                        minHeight: '18px',
                        maxHeight: '18px',
                        minWidth: '18px',
                        maxWidth: '18px',
                        borderRadius: '50%',
                        background: 'rgba(220, 38, 38, 0.95)',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '9px',
                        padding: 0
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Modal Bottom Control Bar (Nút chụp liên tiếp và nút xác nhận) */}
            <div style={{
              padding: isMobile ? '12px 18px calc(14px + env(safe-area-inset-bottom, 12px))' : '14px 20px 24px',
              background: '#0a0f1d',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexShrink: 0
            }}>
              {/* Left: Video Button */}
              <div style={{ width: '70px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => {
                    if (isRecordingVideo) {
                      stopVideoRecording();
                    } else {
                      startVideoRecording();
                    }
                  }}
                  disabled={!isRecordingVideo && mediaList.length >= 5}
                  title={isRecordingVideo ? 'Dừng quay video' : 'Quay video (15s)'}
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '50%',
                    background: isRecordingVideo ? '#dc2626' : 'rgba(239, 68, 68, 0.15)',
                    border: '2px solid #ef4444',
                    color: isRecordingVideo ? 'white' : '#ef4444',
                    cursor: (!isRecordingVideo && mediaList.length >= 5) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    opacity: (!isRecordingVideo && mediaList.length >= 5) ? 0.4 : 1,
                    transition: 'all 0.15s ease'
                  }}
                >
                  <i className={isRecordingVideo ? "fas fa-stop" : "fas fa-video"}></i>
                </button>
                <span style={{ color: '#94a3b8', fontSize: '10px', marginTop: '4px', whiteSpace: 'nowrap' }}>
                  {isRecordingVideo ? 'Dừng quay' : 'Quay video'}
                </span>
              </div>

              {/* Center: Big Shutter Button - Chụp liên tiếp */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={handleCapturePhoto}
                  disabled={mediaList.length >= 5 || isRecordingVideo}
                  title="Chụp ảnh (bấm liên tiếp để chụp nhiều ảnh)"
                  style={{
                    width: '68px',
                    height: '68px',
                    minHeight: '68px',
                    maxHeight: '68px',
                    minWidth: '68px',
                    maxWidth: '68px',
                    borderRadius: '50%',
                    background: 'transparent',
                    border: '4px solid #ffffff',
                    padding: '4px',
                    cursor: (mediaList.length >= 5 || isRecordingVideo) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: (mediaList.length >= 5 || isRecordingVideo) ? 0.35 : 1,
                    boxSizing: 'border-box',
                    transition: 'transform 0.1s ease',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
                  }}
                  onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.92)')}
                  onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  onTouchStart={(e) => (e.currentTarget.style.transform = 'scale(0.92)')}
                  onTouchEnd={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  <div style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: '#ffffff',
                    boxShadow: '0 0 10px rgba(255,255,255,0.6)'
                  }} />
                </button>
                <span style={{ color: '#ffffff', fontSize: '11px', marginTop: '5px', fontWeight: 600 }}>
                  {mediaList.length >= 5 ? 'Đã đủ 5 ảnh' : `Chụp (${mediaList.length}/5)`}
                </span>
              </div>

              {/* Right: Confirm Button (Nút xác nhận) */}
              <div style={{ width: '75px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={stopCameraStream}
                  disabled={mediaList.length < 1}
                  style={{
                    width: '100%',
                    padding: '11px 12px',
                    borderRadius: '10px',
                    background: mediaList.length >= 1 ? '#16a34a' : '#334155',
                    color: 'white',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: '13.5px',
                    cursor: mediaList.length < 1 ? 'not-allowed' : 'pointer',
                    opacity: mediaList.length < 1 ? 0.45 : 1,
                    boxShadow: mediaList.length >= 1 ? '0 3px 10px rgba(22, 163, 74, 0.45)' : 'none',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s ease'
                  }}
                >
                  Xác nhận
                </button>
                <span style={{ color: mediaList.length >= 1 ? '#4ade80' : '#64748b', fontSize: '10.5px', marginTop: '4px', whiteSpace: 'nowrap', fontWeight: 500 }}>
                  {mediaList.length >= 1 ? `✓ Đã có ${mediaList.length}` : 'Cần ≥ 1'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Lightbox Modal */}
      {previewItemModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setPreviewItemModal(null)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '85vh',
              background: '#0f172a',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewItemModal(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.6)',
                color: 'white',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              ✕
            </button>
            {previewItemModal.type === 'image' ? (
              <img
                src={previewItemModal.previewUrl}
                alt={previewItemModal.name}
                style={{
                  maxWidth: '85vw',
                  maxHeight: '78vh',
                  display: 'block',
                  objectFit: 'contain'
                }}
              />
            ) : (
              <video
                src={previewItemModal.previewUrl}
                controls
                autoPlay
                playsInline
                style={{
                  maxWidth: '85vw',
                  maxHeight: '78vh',
                  display: 'block'
                }}
              />
            )}
            <div style={{
              padding: '10px 16px',
              background: '#0f172a',
              color: '#94a3b8',
              fontSize: '13px',
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '1px solid #1e293b'
            }}>
              <span>{previewItemModal.name}</span>
              <span>{(previewItemModal.size / 1024).toFixed(0)} KB</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

